import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import AgentManager from './pages/agent/AgentManager';
import AgentChat from './pages/agent/AgentChat';
import UserRegistration from './pages/auth/UserRegistration';
import Login from './pages/auth/Login';
import UserAccount from './pages/user/UserAccount';
import UserAgents from './pages/user/UserAgents';
import UserConversations from './pages/user/UserConversations';
import MCPToolsAdmin from './pages/mcp/MCPToolsAdmin';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import AgentChatDetail from './pages/agent/AgentChatDetail';
import ChannelsList from './pages/channels/ChannelsList';
import ChannelNew from './pages/channels/ChannelNew';
import ChannelConfigure from './pages/channels/ChannelConfigure';
import ChannelDetails from './pages/channels/ChannelDetails';
import AgentChatIframe from './pages/agent/AgentChatIframe';
import { 
  Bot, Users, User, LogOut, ArrowRight, 
  Wifi, Menu, X, ChevronDown,
  HeadphonesIcon
} from 'lucide-react';

import './App.css';

const config = {
  companyName: "Solteozap IA",
  logoUrl: "/logo-zap.png"
};

const Navigation = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const configMenuRef = useRef(null);

  // Fechar menu de configurações ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (configMenuRef.current && !configMenuRef.current.contains(event.target)) {
        setConfigMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fechar menu mobile ao mudar de rota
  useEffect(() => {
    setMobileMenuOpen(false);
    setConfigMenuOpen(false);
  }, [location]);

  // Páginas onde a navegação deve ser ocultada
  const hiddenNavPages = ['/agent-chat', '/agent-manager', '/agent-chat-detail'];
  const shouldHideNav = hiddenNavPages.some(page => location.pathname.startsWith(page));

  if (shouldHideNav) return null;

  // Links principais com destaque
  const mainLinks = [
    { 
      path: '/conversations', 
      label: 'Atendimentos', 
      icon: HeadphonesIcon,
      description: 'Gerencie conversas com clientes',
      highlight: true,
      badge: 'Principal'
    },
    { 
      path: '/my-agents', 
      label: 'Funcionários', 
      icon: Users,
      description: 'Gerencie seus agentes digitais',
      highlight: true,
      badge: 'Agentes'
    },
    { 
      path: '/channels', 
      label: 'Canais', 
      icon: Wifi,
      description: 'Gerencie seus canais de comunicação',
      highlight: true,
      badge: ''
    }
  ];

  // Links de configuração agrupados
  const configLinks = [
    { 
      path: '/channels', 
      label: 'Canais', 
      icon: Wifi,
      description: 'Configure canais de atendimento'
    }
  ];

  // Função para verificar se o link está ativo
  const isActiveLink = (path) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  // Classes para links principais (destacados)
  const getMainLinkClass = (path, isMobile = false) => {
    const isActive = isActiveLink(path);
    
    if (isMobile) {
      return isActive
        ? "w-full flex items-start gap-3 bg-gradient-to-r from-green-600 to-green-600 text-white p-4 rounded-2xl shadow-lg"
        : "w-full flex items-start gap-3 bg-white border-2 border-gray-200 p-4 rounded-2xl hover:border-green-300 hover:bg-purple-50 transition-all";
    }
    
    return isActive
      ? "relative flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-600 text-white px-5 py-2.5 rounded-2xl font-semibold transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
      : "relative flex items-center gap-2 bg-white border-2 border-green-200 text-green-700 px-5 py-2.5 rounded-2xl font-semibold hover:bg-purple-50 hover:border-green-300 transition-all transform hover:scale-[1.02]";
  };

  // Classes para links de configuração
  const getConfigLinkClass = (path) => {
    const isActive = isActiveLink(path);
    return isActive
      ? "block w-full px-4 py-3 bg-gradient-to-r from-green-600 to-green-600 text-white rounded-xl transition-all"
      : "block w-full px-4 py-3 text-gray-700 hover:bg-purple-50 rounded-xl transition-all hover:text-green-600";
  };

  return (
    <nav className="bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 lg:h-20">
          {/* Logo e Links Principais Desktop */}
          <div className="flex items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 hover:scale-[1.02] transition-transform">
              {config.logoUrl ? (
                <img 
                  src={config.logoUrl} 
                  alt={config.companyName} 
                  className="w-10 h-10 lg:w-12 lg:h-12 object-contain"
                />
              ) : (
                <div className="bg-gradient-to-r from-green-600 to-green-600 p-2.5 rounded-2xl shadow-lg">
                  <Bot className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                </div>
              )}
              <span className="bg-gradient-to-r from-green-600 to-green-600 text-transparent bg-clip-text hidden sm:inline text-xl lg:text-2xl font-bold">
                {config.companyName}
              </span>
            </Link>
            
            {/* Links Principais - Desktop Only */}
            {currentUser && (
              <div className="hidden lg:flex items-center ml-8 xl:ml-12 gap-3">
                {mainLinks.map(link => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={getMainLinkClass(link.path)}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{link.label}</span>
                      {link.badge && (
                        <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full shadow-md">
                          {link.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Ações do Usuário e Menu Mobile */}
          <div className="flex items-center gap-3">
            {currentUser ? (
              <>
                {/* Perfil do Usuário - Desktop */}
                <Link 
                  to="/account" 
                  className={`hidden lg:flex items-center gap-3 px-4 py-2 rounded-2xl transition-all ${
                    isActiveLink('/account') 
                      ? 'bg-gradient-to-r border-2 border-green-300' 
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <div className="w-9 h-9 bg-gradient-to-br  rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-900">
                      {currentUser?.name?.substring(0, 15)}
                    </div>
                    </div>
                </Link>
                
                {/* Botão Sair - Desktop */}
                <button
                  onClick={logout}
                  className="hidden lg:flex items-center gap-2 px-4 py-2.5 text-red-600 hover:text-red-700 hover:bg-red-50 font-medium transition-all rounded-2xl border-2 border-transparent hover:border-red-200"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sair</span>
                </button>
                
                {/* Menu Mobile Toggle */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2.5 hover:bg-gray-100 rounded-2xl transition-all"
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6 text-gray-700" />
                  ) : (
                    <Menu className="w-6 h-6 text-gray-700" />
                  )}
                </button>
              </>
            ) : (
              <>
                {/* Links Públicos - Desktop */}
                <div className="hidden sm:flex items-center gap-3">
                  <Link
                    to="/login"
                    className="flex items-center gap-2 text-gray-700 hover:text-green-600 px-4 py-2 font-medium transition-all"
                  >
                    Entrar
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-600 text-white px-5 py-2.5 rounded-2xl font-semibold hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                  >
                    Criar conta grátis
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                
                {/* Menu Mobile Toggle - Público */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="sm:hidden p-2.5 hover:bg-gray-100 rounded-2xl transition-all"
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6 text-gray-700" />
                  ) : (
                    <Menu className="w-6 h-6 text-gray-700" />
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Menu Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-black/30 backdrop-blur-sm z-40" onClick={() => setMobileMenuOpen(false)} />
      )}
      
      {/* Menu Mobile Content */}
      <div className={`lg:hidden fixed top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-2xl transform transition-all duration-300 z-50 ${
        mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
      }`}>
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto p-4">
          {currentUser ? (
            <>
              {/* Perfil Mobile */}
              <Link 
                to="/account" 
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl mb-4 border-2 border-green-200"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="text-left">
 <div className="text-sm font-semibold text-gray-900">
   {currentUser?.name?.substring(0, 15)}
 </div>
</div>
                </div>
                <ChevronDown className="w-5 h-5 text-gray-400 -rotate-90" />
              </Link>
              
              {/* Links Principais Mobile */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2">
                  Principais
                </h3>
                <div className="space-y-3">
                  {mainLinks.map(link => {
                    const Icon = link.icon;
                    const isActive = isActiveLink(link.path);
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={getMainLinkClass(link.path, true)}
                      >
                        <div className={`
                          w-12 h-12 rounded-xl flex items-center justify-center
                          ${isActive ? 'bg-white/20' : 'bg-purple-100'}
                        `}>
                          <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-green-600'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold flex items-center gap-2">
                            {link.label}
                            {link.badge && (
                              <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                                {link.badge}
                              </span>
                            )}
                          </div>
                          <div className={`text-sm mt-0.5 ${isActive ? 'text-white/80' : 'text-gray-600'}`}>
                            {link.description}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
              
              {/* Botão Sair Mobile */}
              <button
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 hover:bg-red-100 font-semibold transition-all rounded-2xl border-2 border-red-200"
              >
                <LogOut className="w-5 h-5" />
                <span>Sair da conta</span>
              </button>
            </>
          ) : (
            <>
              {/* Links Públicos Mobile */}
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="w-full flex items-center justify-center gap-2 p-4 border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-semibold rounded-2xl transition-all"
                >
                  Entrar na conta
                </Link>
                <Link
                  to="/register"
                  className="w-full flex items-center justify-center gap-2 p-4 bg-gradient-to-r from-green-600 to-green-600 text-white font-semibold rounded-2xl shadow-lg"
                >
                  Criar conta grátis
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

// Componente wrapper para prover location context
const AppContent = () => {
  return (
    <>
      <Navigation />
      <Routes>
        <Route path="/" element={<></>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<UserRegistration />} />
        <Route path="/agent-chat/iframe/:id" element={<AgentChatIframe />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/agent-manager" element={<AgentManager />} />
          <Route path="/agent-manager/:id" element={<AgentManager />} />
          <Route path="/agent-chat-detail/:id" element={<AgentChatDetail />} />
          <Route path="/agent-chat" element={<AgentChat />} />
          <Route path="/agent-chat/:id" element={<AgentChat />} />
          <Route path="/my-agents" element={<UserAgents />} />
          <Route path="/conversations" element={<UserConversations />} />
          <Route path="/account" element={<UserAccount />} />
          <Route path="/mcp" element={<MCPToolsAdmin />} />
          <Route path="/channels" element={<ChannelsList />} />
          <Route path="/channels/new" element={<ChannelNew />} />
          <Route path="/channels/:channelId/configure" element={<ChannelConfigure />} />
          <Route path="/channels/:channelId" element={<ChannelDetails />} />
        </Route>
      </Routes>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
          <AppContent />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;

export { config };